import { useState, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Upload, FileSpreadsheet, X } from 'lucide-react'
import { technologiesApi } from '../api'
import { useToast } from '@/hooks/use-toast'

interface TechnologyImportDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
}

interface ImportResult {
    totalInFile: number
    imported: number
    skipped: number
    errorCount: number
    errors: Array<{ row: number; model?: string; error: string }>
}

export function TechnologyImportDialog({ open, onOpenChange }: TechnologyImportDialogProps) {
    const { t } = useTranslation()
    const { toast } = useToast()
    const queryClient = useQueryClient()
    const [file, setFile] = useState<File | null>(null)
    const [uploading, setUploading] = useState(false)
    const [result, setResult] = useState<ImportResult | null>(null)
    const [error, setError] = useState<string | null>(null)
    const [dragOver, setDragOver] = useState(false)
    const inputRef = useRef<HTMLInputElement>(null)

    const reset = useCallback(() => {
        setFile(null); setResult(null); setError(null); setUploading(false)
    }, [])

    const handleClose = (next: boolean) => {
        if (!next) reset()
        onOpenChange(next)
    }

    const pickFile = (f: File | undefined) => {
        if (!f) return
        const ok = /\.(csv|xlsx|xls)$/i.test(f.name)
        if (!ok) { setError(t('technologies.import.invalidType', 'Please select a CSV or Excel file')); return }
        setError(null); setResult(null); setFile(f)
    }

    const handleImport = async () => {
        if (!file) return
        setUploading(true); setError(null)
        try {
            const res = await technologiesApi.import(file)
            const data = (res.data as any)?.data ?? res.data
            setResult(data)
            queryClient.invalidateQueries({ queryKey: ['technologies'] })
            if (data.imported > 0) {
                toast({ title: t('common.success', 'Success'), description: `${data.imported} technologies imported` })
            }
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.message || 'Import failed')
        } finally {
            setUploading(false)
        }
    }

    return (
        <Dialog open={open} onOpenChange={handleClose}>
            <DialogContent className="sm:max-w-2xl max-h-[90vh] flex flex-col">
                <DialogHeader>
                    <DialogTitle>{t('technologies.import.title', 'Import Technologies')}</DialogTitle>
                    <DialogDescription>
                        {t('technologies.import.description', 'Upload a CSV or Excel file. Required columns: Model and Manufacturer (optional: Category, Description). Manufacturer and Category are matched by name; rows with an unknown name are skipped and listed below.')}
                    </DialogDescription>
                </DialogHeader>

                <div className="flex-1 overflow-y-auto space-y-4">
                    {!result && (
                        <div
                            className={'rounded-lg border-2 border-dashed p-8 text-center cursor-pointer transition-colors ' + (dragOver ? 'border-primary bg-primary/5' : 'border-muted')}
                            onClick={() => inputRef.current?.click()}
                            onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
                            onDragLeave={() => setDragOver(false)}
                            onDrop={(e) => { e.preventDefault(); setDragOver(false); pickFile(e.dataTransfer.files[0]) }}
                        >
                            <input ref={inputRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={(e) => pickFile(e.target.files?.[0])} />
                            {file ? (
                                <div className="flex items-center justify-center gap-2">
                                    <FileSpreadsheet className="h-5 w-5 text-blue-500" />
                                    <span className="text-sm font-medium">{file.name}</span>
                                    <button onClick={(e) => { e.stopPropagation(); setFile(null) }} className="p-0.5 rounded hover:bg-muted"><X className="h-4 w-4" /></button>
                                </div>
                            ) : (
                                <div className="flex flex-col items-center gap-2">
                                    <Upload className="h-8 w-8 text-muted-foreground" />
                                    <span className="text-sm font-medium">{t('technologies.import.dropzone', 'Click or drag a CSV/Excel file here')}</span>
                                    <span className="text-xs text-muted-foreground">Model, Manufacturer (Category optional)</span>
                                </div>
                            )}
                        </div>
                    )}

                    {error && <div className="rounded-md bg-destructive/10 text-destructive text-sm p-3">{error}</div>}

                    {result && (
                        <div className="space-y-3">
                            <div className="grid grid-cols-3 gap-3 text-center">
                                <div className="rounded-lg border p-3"><div className="text-2xl font-bold text-green-600">{result.imported}</div><div className="text-xs text-muted-foreground">Imported</div></div>
                                <div className="rounded-lg border p-3"><div className="text-2xl font-bold text-amber-600">{result.skipped}</div><div className="text-xs text-muted-foreground">Skipped (duplicate)</div></div>
                                <div className="rounded-lg border p-3"><div className="text-2xl font-bold text-destructive">{result.errorCount}</div><div className="text-xs text-muted-foreground">Errors</div></div>
                            </div>
                            {result.errors.length > 0 && (
                                <div className="rounded-md border max-h-48 overflow-y-auto">
                                    <table className="w-full text-xs">
                                        <thead className="sticky top-0 bg-muted/80"><tr><th className="text-left px-3 py-1.5">Row</th><th className="text-left px-3 py-1.5">Model</th><th className="text-left px-3 py-1.5">Error</th></tr></thead>
                                        <tbody>
                                            {result.errors.map((e, i) => (
                                                <tr key={i} className="border-t"><td className="px-3 py-1.5">{e.row}</td><td className="px-3 py-1.5">{e.model || '—'}</td><td className="px-3 py-1.5 text-destructive">{e.error}</td></tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <DialogFooter>
                    {result ? (
                        <Button onClick={() => handleClose(false)}>{t('common.close', 'Close')}</Button>
                    ) : (
                        <>
                            <Button variant="outline" onClick={() => handleClose(false)}>{t('common.cancel', 'Cancel')}</Button>
                            <Button onClick={handleImport} disabled={!file || uploading}>
                                {uploading ? t('common.uploading', 'Uploading…') : t('technologies.import.importButton', 'Import')}
                            </Button>
                        </>
                    )}
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
